import { captureUnsavedFeedback } from '../utils/unsavedFeedback';
import SlidingChoice from '../components/SlidingChoice';
import MoneyInput from '../components/MoneyInput';
import { parseAmount, formatAmountInput } from '../utils/currencyInput';
import VehicleContactFields from '../components/VehicleContactFields';
import PracticeDocumentLinks from '../components/PracticeDocumentLinks';
import FileUpload from '../components/FileUpload';
import React, { useEffect, useState } from 'react';
import { FaCheck, FaDownload, FaEye, FaFileUpload, FaTrash } from 'react-icons/fa';
import QuickAddressInput from '../components/QuickAddressInput';
import { documentDefinition, latestDocumentFor } from '../utils/practiceDocuments';
import './DannoAuto.css'; // Importa il file CSS

const vehicleDocumentKeys = {
  perizia: 'appraisal', preventivo: 'repairEstimate', fotoLavori: 'repairPhotos',
  fattura: 'repairInvoice', cessioneCredito: 'creditAssignment', cessazione: 'cessation'
};

function DannoAuto({ materialDamage = false, data, setData, schedaId, view = 'all', operator, onPersistedData, onVehicleStatusChange, onPracticeStatusChange, responsabilitaControparte }) {
  const [localData, setLocalData] = useState({});
  const values = data ?? localData;
  const update = setData ?? setLocalData;
  const field = (name, initial) => [values[name] ?? initial, value => update(previous => ({ ...previous, [name]: value }))];
  // Stato per gestire lo scenario (Demolizione o Riparazione)
  const [scenario, setScenario] = field('scenario', '');
  const [vehicleUploadBusy, setVehicleUploadBusy] = useState('');
  const [vehicleUploadError, setVehicleUploadError] = useState('');
  const [archiveDocuments, setArchiveDocuments] = useState([]);
  const [repairerOpen, setRepairerOpen] = useState(false);
  const [repairers, setRepairers] = useState([]);
  const [repairerSearch, setRepairerSearch] = useState('');
  const [repairerError, setRepairerError] = useState('');
  const [creatingRepairer, setCreatingRepairer] = useState(false);
  const [newRepairer, setNewRepairer] = useState({ nome: '', referente: '', indirizzo: '', citta: '', cap: '', provincia: '', telefono: '', email: '' });
  const [repairerDraft, setRepairerDraft] = useState({});
  const [repairerDetails, setRepairerDetails] = useState(null);
  const [repairerDetailsOpen, setRepairerDetailsOpen] = useState(false);
  const [expertOpen, setExpertOpen] = useState(false);
  const [experts, setExperts] = useState([]);
  const [expertSearch, setExpertSearch] = useState('');
  const [expertDraft, setExpertDraft] = useState({});
  const [expertDetails, setExpertDetails] = useState(null);
  const [expertError, setExpertError] = useState('');
  const [creatingExpert, setCreatingExpert] = useState(false);
  const [newExpert, setNewExpert] = useState({ nome: '', referente: '', indirizzo: '', citta: '', cap: '', provincia: '', telefono: '', email: '' });
  const [settlementEditor, setSettlementEditor] = useState(null);
  const [settlementBusy, setSettlementBusy] = useState(false);
  const [settlementError, setSettlementError] = useState('');
  const repairStatusLabels = {
    da_definire: 'Modalità da definire',
    riparazione: 'Riparazione ordinaria presso riparatore',
    riparazione_economia: 'Riparazione in economia presso riparatore',
    non_ripara: 'Il cliente non intende riparare'
  };

  const loadVehicleDocuments = async () => {
    if (!schedaId) return [];
    const response = await fetch(`/api/documenti/schede/${schedaId}`);
    if (!response.ok) throw new Error('Archivio documenti non disponibile');
    const rows = await response.json(); setArchiveDocuments(rows); return rows;
  };

  useEffect(() => { loadVehicleDocuments().catch(() => setArchiveDocuments([])); }, [schedaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadVehicleDocument = async (event, kind) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !schedaId) return;
    setVehicleUploadBusy(kind); setVehicleUploadError('');
    const definition = documentDefinition(vehicleDocumentKeys[kind]);
    try {
      if (file.size > 15 * 1024 * 1024) throw new Error('Il file supera il limite di 15 MB');
      const response = await fetch(`/api/documenti/schede/${schedaId}`, { method: 'POST', headers: {
        'Content-Type': 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name),
        'X-File-Type': file.type, 'X-Category': definition.area, 'X-Description': encodeURIComponent(definition.label)
      }, body: file });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Caricamento non riuscito');
      update(previous => ({ ...previous, [`${kind}DocumentoId`]: result.id, [`${kind}DocumentoNome`]: file.name }));
      await loadVehicleDocuments();
    } catch (error) { setVehicleUploadError(error.message); } finally { setVehicleUploadBusy(''); }
  };

  const documentControl = (kind, label) => {
    const definition = documentDefinition(vehicleDocumentKeys[kind]);
    const archived = latestDocumentFor(archiveDocuments, definition);
    const id = values[`${kind}DocumentoId`] || archived?.id;
    const name = values[`${kind}DocumentoNome`] || archived?.nome_originale;
    return <div className={`vehicle-document ${id ? 'complete' : ''}`}>
      <div>{id ? <FaCheck /> : <FaFileUpload />}<span><strong>{label}</strong><small>{name || 'Nessun documento caricato'}</small></span></div>
      <div className="vehicle-document-actions">
        {id && <><a href={`/api/documenti/${id}/file?preview=1`} target="_blank" rel="noreferrer" title="Anteprima"><FaEye /></a><a href={`/api/documenti/${id}/file`} title="Scarica"><FaDownload /></a><button type="button" className="vehicle-delete-document" title="Elimina documento" aria-label={`Elimina ${label}`} onClick={async () => { if (!window.confirm(`Eliminare definitivamente “${name || label}”?`)) return; setVehicleUploadBusy(kind); setVehicleUploadError(''); try { const response = await fetch(`/api/documenti/${id}`, { method: 'DELETE' }); if (!response.ok) { const result = await response.json().catch(() => ({})); throw new Error(result.error || 'Eliminazione non riuscita'); } update(previous => ({ ...previous, [`${kind}DocumentoId`]: '', [`${kind}DocumentoNome`]: '' })); await loadVehicleDocuments(); } catch (error) { setVehicleUploadError(error.message); } finally { setVehicleUploadBusy(''); } }}><FaTrash /></button></>}
        <FileUpload compact iconOnly showSelectedName={false} label={vehicleUploadBusy === kind ? 'Caricamento…' : id ? 'Sostituisci' : 'Carica'} accept=".pdf,.doc,.docx,.jpg,.jpeg,.jfif,.png" disabled={Boolean(vehicleUploadBusy)} onChange={event => uploadVehicleDocument(event, kind)} />
      </div>
    </div>;
  };

  const loadRepairers = async (search = '') => {
    try {
      const response = await fetch(`/api/contatti?ricerca=${encodeURIComponent(search)}`);
      if (!response.ok) throw new Error('Impossibile caricare le riparatori');
      setRepairers((await response.json()).filter(item => ['Carrozzeria','Riparatore'].includes(item.tipo)));
      setRepairerError('');
    } catch (error) { setRepairerError(error.message); }
  };

  useEffect(() => {
    if (!repairerOpen) return undefined;
    if (repairerSearch.trim().length < 2) { setRepairers([]); return undefined; }
    const timer = setTimeout(() => loadRepairers(repairerSearch), 180);
    return () => clearTimeout(timer);
  }, [repairerOpen, repairerSearch]);

  useEffect(() => {
    if (!values.riparatoreId) { setRepairerDetails(null); return; }
    let active = true;
    fetch(`/api/contatti/${values.riparatoreId}`).then(response => {
      if (!response.ok) throw new Error('Riparatore non disponibile');
      return response.json();
    }).then(result => { if (active) setRepairerDetails(result); }).catch(() => { if (active) setRepairerDetails(null); });
    return () => { active = false; };
  }, [values.riparatoreId]);

  useEffect(() => {
    if (!expertOpen) return undefined;
    if (expertSearch.trim().length < 2) { setExperts([]); return undefined; }
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`/api/contatti?tipo=Perito&ricerca=${encodeURIComponent(expertSearch)}`);
        if (!response.ok) throw new Error('Impossibile caricare i periti');
        setExperts(await response.json()); setExpertError('');
      } catch (error) { setExpertError(error.message); }
    }, 180);
    return () => clearTimeout(timer);
  }, [expertOpen, expertSearch]);

  useEffect(() => {
    if (!values.peritoId) { setExpertDetails(null); return; }
    let active = true;
    fetch(`/api/contatti/${values.peritoId}`).then(response => response.ok ? response.json() : null)
      .then(result => { if (active) setExpertDetails(result); }).catch(() => { if (active) setExpertDetails(null); });
    return () => { active = false; };
  }, [values.peritoId]);

  const openExpert = () => { setExpertDraft({ peritoId: values.peritoId || '', peritoNome: values.peritoNome || '' }); setExpertOpen(true); };
  const saveExpert = () => { update(previous => ({ ...previous, ...expertDraft })); setExpertOpen(false); };
  const removeExpert = () => update(previous => ({ ...previous, peritoId: '', peritoNome: '' }));
  const createExpert = async () => {
    if (!newExpert.nome.trim()) return setExpertError('Inserisci il nome del perito o dello studio peritale');
    try {
      const response = await fetch('/api/contatti', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...newExpert, tipo: 'Perito', tipo_soggetto: 'azienda' }) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Impossibile creare il perito');
      setExpertDraft({ peritoId: result.id, peritoNome: newExpert.nome.trim() }); setCreatingExpert(false);
      setNewExpert({ nome: '', referente: '', indirizzo: '', citta: '', cap: '', provincia: '', telefono: '', email: '' });
      setExpertError('');
    } catch (error) { setExpertError(error.message); }
  };

  const openRepairer = () => {
    setRepairerDraft({
      riparatoreId: values.riparatoreId || '', riparatoreNome: values.riparatoreNome || ''
    });
    setRepairerOpen(true);
  };
  const saveRepairer = () => {
    update(previous => ({ ...previous, ...repairerDraft, statoRiparazione: previous.statoRiparazione || (repairerDraft.riparatoreId ? 'riparazione' : 'da_definire') }));
    setRepairerOpen(false);
  };
  const removeRepairer = () => update(previous => ({ ...previous, riparatoreId: '', riparatoreNome: '', statoRiparazione: 'da_definire', luogoVisioneTipo: previous.luogoVisioneTipo === 'altro' ? 'altro' : 'riparatore' }));
  const createRepairer = async () => {
    if (!newRepairer.nome.trim()) return setRepairerError('Inserisci il nome delil riparatore');
    try {
      const response = await fetch('/api/contatti', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...newRepairer, tipo: 'Riparatore', tipo_soggetto: 'azienda' }) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Impossibile creare il riparatore');
      setRepairerDraft(previous => ({ ...previous, riparatoreId: result.id, riparatoreNome: newRepairer.nome.trim() }));
      setCreatingRepairer(false);
      setNewRepairer({ nome: '', referente: '', indirizzo: '', citta: '', cap: '', provincia: '', telefono: '', email: '' });
      await loadRepairers('');
    } catch (error) { setRepairerError(error.message); }
  };

  // Stato per i valori di input (Demolizione)
  const valoreCommerciale = values.valoreCommercialePerizia ?? values.valoreCommerciale ?? '0,00';
  const setValoreCommerciale = value => update(previous => ({ ...previous, valoreCommercialePerizia: value }));
  const valoreRelitto = values.valoreRelittoPerizia ?? values.valoreRelitto ?? '0,00';
  const setValoreRelitto = value => update(previous => ({ ...previous, valoreRelittoPerizia: value }));
  const [immatricolazione, setImmatricolazione] = field('immatricolazione', '0,00');
  const [fram, setFram] = field('fram', '0,00');
  const [bolloNonGoduto, setBolloNonGoduto] = field('bolloNonGoduto', '0,00');
  const [speseDemolizione, setSpeseDemolizione] = field('speseDemolizione', '0,00');
  const [deposito, setDeposito] = field('deposito', '0,00');
  const [traino, setTraino] = field('traino', '0,00');
  const [accessori, setAccessori] = field('accessori', '0,00');

  // Stato per i valori di input (Riparazione)
  const perizia = values.stimaRiparazionePerizia ?? values.perizia ?? '0,00';
  const [preventivo, setPreventivo] = field('preventivo', '0,00');
  const [fattura, setFattura] = field('fattura', '0,00');
  const [speseNoleggio, setSpeseNoleggio] = field('speseNoleggio', '0,00');
  const [fermoTecnico, setFermoTecnico] = field('fermoTecnico', '0,00');
  const [trainoRiparazione, setTrainoRiparazione] = field('trainoRiparazione', '0,00');
  const [svalutazione, setSvalutazione] = field('svalutazione', '0,00');
  const [accessoriRiparazione, setAccessoriRiparazione] = field('accessoriRiparazione', '0,00');

  // Stato per l'acconto, la definizione e gli onorari
  const [acconto, setAcconto] = field('acconto', '0,00');
  const [definizione, setDefinizione] = field('definizione', '0,00');
  const [onorari, setOnorari] = field('onorari', '0,00');

  // Stato per selezionare una voce tra Perizia, Preventivo o Fattura
  const [voceSelezionata, setVoceSelezionata] = field('voceSelezionata', 'perizia');

  // Funzione per formattare i numeri con due decimali e virgola
  const formatValue = formatAmountInput;
  const numericValue = parseAmount;

  // Calcolo del totale per Demolizione
  const totaleDemolizione = formatValue(
    numericValue(valoreCommerciale) - numericValue(valoreRelitto) +
    numericValue(immatricolazione) + numericValue(fram) + numericValue(bolloNonGoduto) +
    numericValue(speseDemolizione) + numericValue(deposito) + numericValue(traino) +
    numericValue(accessori)
  );

  // Calcolo del totale per Riparazione
  const totaleRiparazione = formatValue(
    (voceSelezionata === 'perizia' ? numericValue(perizia) : 0) +
    (voceSelezionata === 'preventivo' ? numericValue(preventivo) : 0) +
    (voceSelezionata === 'fattura' ? numericValue(fattura) : 0) +
    numericValue(speseNoleggio) + numericValue(fermoTecnico) + numericValue(trainoRiparazione) +
    numericValue(svalutazione) + numericValue(accessoriRiparazione)
  );

  const responsibilityNumber = Number(String(responsabilitaControparte ?? '').replace(',', '.'));
  const responsibilityDefined = responsabilitaControparte !== '' && responsabilitaControparte != null && Number.isFinite(responsibilityNumber);
  const totaleLordo = numericValue(scenario === 'demolizione' ? totaleDemolizione : totaleRiparazione);
  const totaleSpettante = responsibilityDefined ? formatValue(totaleLordo * responsibilityNumber / 100) : null;

  // Calcolo automatico dell'integrazione
  const integrazione = formatValue(
    numericValue(definizione) - numericValue(acconto)
  );
  const showRepairer = ['riparazione', 'riparazione_economia'].includes(values.statoRiparazione);
  const showManagement = view !== 'trattativa';
  const showCalculation = view !== 'gestione';
  const settlement = values.definizioneVeicolo;
  const operatorName = operator?.nome || operator?.username || '';
  const openDefinition = () => {
    const suggestedAmount = numericValue(definizione) > 0 ? definizione : '';
    const beneficiary = values.modalitaGestioneCredito === 'cessione'
      ? (repairerDetails?.nome || values.riparatoreNome || '')
      : 'Cliente';
    setSettlementError('');
    setSettlementEditor({ type: 'definition', importo: suggestedAmount, onorari, data: new Date().toISOString().slice(0, 10), beneficiario: beneficiary, acconto, note: '' });
  };
  const openPayment = () => {
    setSettlementError('');
    setSettlementEditor({ type: 'payment', importo: String(settlement?.saldo ?? '').replace('.', ','), data: new Date().toISOString().slice(0, 10), note: '' });
  };
  const saveSettlement = async () => {
    const commitFeedback = captureUnsavedFeedback(document.querySelector('.repairer-overlay')); 
    if (!settlementEditor || settlementBusy) return;
    setSettlementBusy(true); setSettlementError('');
    try {
      const endpoint = settlementEditor.type === 'definition' ? 'definizione-veicolo' : 'pagamento-veicolo';
      const response = await fetch(`/api/workflow/schede/${schedaId}/${endpoint}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...settlementEditor, operatore: operatorName }) });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Operazione non salvata');
      commitFeedback();
      if (onPersistedData) onPersistedData(result.dannoAuto); else update(result.dannoAuto);
      onVehicleStatusChange?.(result.stato_veicolo);
      if (result.stato) onPracticeStatusChange?.(result.stato);
      setSettlementEditor(null);
    } catch (error) { setSettlementError(error.message); } finally { setSettlementBusy(false); }
  };

  return (
    <div className="danno-auto-container">
      <h1>{view === 'trattativa' ? (materialDamage ? 'Trattativa danni materiali' : 'Trattativa del danno al veicolo') : (materialDamage ? 'Gestione danni materiali' : 'Gestione del danno al veicolo')}</h1>

      {showManagement && <>
      <section className="vehicle-flow-card">
        <div className="vehicle-flow-heading"><div><span className="repairer-eyebrow">1 · Perizia</span><h2>Esito economico della perizia</h2></div></div>
        <div className="appraisal-expert">
          <div><span>Perito collegato alla Rubrica</span><strong>{expertDetails?.nome || values.peritoNome || 'Da indicare'}</strong>
            {expertDetails && <small>{expertDetails.telefoni?.[0]?.telefono && <a href={`tel:${expertDetails.telefoni[0].telefono.replace(/[^+\d]/g, '')}`}>{expertDetails.telefoni[0].telefono}</a>}{expertDetails.emails?.[0]?.email && <a href={`mailto:${expertDetails.emails[0].email}`}>{expertDetails.emails[0].email}</a>}</small>}
          </div>
          <span className="appraisal-expert-actions"><button type="button" onClick={openExpert}>{values.peritoId ? 'Cambia' : 'Scegli perito'}</button>{values.peritoId && <button type="button" className="unlink" onClick={removeExpert} title="Scollega il perito">×</button>}</span>
        </div>
        <div className="appraisal-choice" role="group" aria-label="Stato della perizia">
          <button type="button" className={(values.statoPeriziaVeicolo || 'attesa') === 'attesa' ? 'active' : ''} onClick={() => update(previous => ({ ...previous, statoPeriziaVeicolo: 'attesa' }))}>In attesa di perizia</button>
          <button type="button" className={values.statoPeriziaVeicolo === 'rientrata' ? 'active complete' : ''} onClick={() => update(previous => ({ ...previous, statoPeriziaVeicolo: 'rientrata' }))}>Informazioni perizia acquisite</button>
        </div>
        {values.statoPeriziaVeicolo === 'rientrata' && <div className="appraisal-values">
          <label><span>Valore commerciale</span><div><b>€</b><MoneyInput inputMode="decimal" value={valoreCommerciale} onFocus={event => event.target.select()} onBlur={event => setValoreCommerciale(formatValue(event.target.value))} onChange={event => setValoreCommerciale(event.target.value)} /></div></label>
          <label><span>Valore del relitto</span><div><b>€</b><MoneyInput inputMode="decimal" value={valoreRelitto} onFocus={event => event.target.select()} onBlur={event => setValoreRelitto(formatValue(event.target.value))} onChange={event => setValoreRelitto(event.target.value)} /></div></label>
          <label><span>Stima della riparazione <small>facoltativa</small></span><div><b>€</b><MoneyInput inputMode="decimal" value={perizia} onFocus={event => event.target.select()} onBlur={event => update(previous => ({ ...previous, stimaRiparazionePerizia: formatValue(event.target.value) }))} onChange={event => update(previous => ({ ...previous, stimaRiparazionePerizia: event.target.value }))} /></div></label>
        </div>}
        <div className="vehicle-appraisal-documents">
          {documentControl('perizia', 'Perizia')}
          {documentControl('preventivo', 'Preventivo di parte')}
        </div>
      </section>

      <section className="vehicle-decision-card">
        <div><span className="repairer-eyebrow">2 · Decisione</span><h2>Che cosa si fa del veicolo?</h2><p>La scelta determina i documenti da raccogliere e la quantificazione.</p></div>
        <SlidingChoice label="Decisione sul veicolo" value={scenario} onChange={setScenario} options={[{value:'',label:'Da decidere'},{value:'riparazione',label:'Riparazione'},{value:'demolizione',label:'Demolizione'}]} />
      </section>

      {vehicleUploadError && <p className="vehicle-upload-error" role="alert">{vehicleUploadError}</p>}

      {scenario === 'riparazione' && <section className="repair-mode-card">
        <span className="repairer-eyebrow">3 · Modalità di riparazione</span>
        <label><span>Come viene gestita la riparazione?</span><select value={values.statoRiparazione || 'da_definire'} onChange={event => update(previous => ({ ...previous, statoRiparazione: event.target.value }))}>{Object.entries(repairStatusLabels).map(([value, labelText]) => <option value={value} key={value}>{labelText}</option>)}</select></label>
      </section>}

      {scenario === 'riparazione' && showRepairer && <><section className="credit-management-card">
        <div className="credit-management-heading"><span className="repairer-eyebrow">Titolarità del credito</span><strong>Gestione del danno materiale</strong></div>
        <div className="credit-management-choice" role="group" aria-label="Modalità di gestione del danno materiale">
          <button type="button" className={(values.modalitaGestioneCredito || 'cliente') === 'cliente' ? 'active' : ''} onClick={() => update(previous => ({ ...previous, modalitaGestioneCredito: 'cliente' }))}>Per il cliente</button>
          <button type="button" className={values.modalitaGestioneCredito === 'cessione' ? 'active' : ''} onClick={() => update(previous => ({ ...previous, modalitaGestioneCredito: 'cessione' }))}>Cessione del credito</button>
        </div>
        {values.modalitaGestioneCredito === 'cessione' && <div className="credit-assignment-details">
          <div className="credit-assignment-badge">Il riparatore collegata è cessionaria del credito</div>
          <label><span>Data della cessione</span><input type="date" value={values.dataCessioneCredito || ''} onChange={event => update(previous => ({ ...previous, dataCessioneCredito: event.target.value }))} /></label>
          <label><span>Importo ceduto, se già noto</span><div className="credit-amount"><span>€</span><MoneyInput inputMode="decimal" value={values.importoCreditoCeduto || ''} onChange={event => update(previous => ({ ...previous, importoCreditoCeduto: event.target.value }))} /></div></label>
          {!values.riparatoreId && <p>Seleziona il riparatore che ha ricevuto la cessione.</p>}
          {documentControl('cessioneCredito', 'Cessione del credito')}
        </div>}
      </section>

      <section className="repairer-card">
        <div className="repairer-card-main">
          <span className="repairer-eyebrow">{values.modalitaGestioneCredito === 'cessione' ? 'Riparatore cessionario del credito' : 'Riparatore e visione del veicolo'}</span>
          {values.riparatoreId ? <button type="button" className="repairer-name" onClick={() => setRepairerDetailsOpen(true)}>{repairerDetails?.nome || values.riparatoreNome || 'Riparatore collegata'}</button> : <strong>Nessun riparatore collegato</strong>}
          {values.modalitaGestioneCredito === 'cessione' && values.riparatoreId && <span className="credit-role-badge">Cessionaria del credito</span>}
          <small>{values.luogoVisioneTipo === 'altro' ? (values.luogoVisioneIndirizzo || 'Altro luogo da indicare') : (values.riparatoreNome ? 'Veicolo visionabile presso il riparatore' : 'Luogo di visione da definire')}</small>
          {repairerDetails && <span className="repairer-card-contacts">
            {repairerDetails.telefoni?.[0]?.telefono && <a href={`tel:${repairerDetails.telefoni[0].telefono.replace(/[^+\d]/g, '')}`}>{repairerDetails.telefoni[0].telefono}</a>}
            {repairerDetails.emails?.[0]?.email && <a href={`mailto:${repairerDetails.emails[0].email}`}>{repairerDetails.emails[0].email}</a>}
          </span>}
        </div>
        <div className="repairer-card-actions"><button type="button" onClick={openRepairer}>{values.riparatoreId ? 'Cambia' : 'Scegli riparatore'}</button>{values.riparatoreId && <button type="button" className="unlink" onClick={removeRepairer} title="Scollega il riparatore">×</button>}</div>
        <label className="repairer-other-toggle"><input role="switch" aria-label="Veicolo visionabile in un altro luogo" type="checkbox" checked={values.luogoVisioneTipo === 'altro'} onChange={event => update(previous => ({ ...previous, luogoVisioneTipo: event.target.checked ? 'altro' : 'riparatore' }))} /><i aria-hidden="true"/><span>Veicolo visionabile in un altro luogo</span></label>
        {values.luogoVisioneTipo === 'altro' && <div className="repairer-inline-grid">
          <label className="repairer-field wide"><span>Indirizzo completo del luogo</span><input value={values.luogoVisioneIndirizzo || ''} onChange={event => update(previous => ({ ...previous, luogoVisioneIndirizzo: event.target.value }))} /></label>
          <label className="repairer-field"><span>Referente</span><input value={values.luogoVisioneReferente || ''} onChange={event => update(previous => ({ ...previous, luogoVisioneReferente: event.target.value }))} /></label>
          <label className="repairer-field"><span>Telefono</span><input value={values.luogoVisioneTelefono || ''} onChange={event => update(previous => ({ ...previous, luogoVisioneTelefono: event.target.value }))} /></label>
          <label className="repairer-field"><span>Disponibilità / orari</span><input value={values.luogoVisioneDisponibilita || ''} onChange={event => update(previous => ({ ...previous, luogoVisioneDisponibilita: event.target.value }))} /></label>
          <label className="repairer-field"><span>Note</span><input value={values.luogoVisioneNote || ''} onChange={event => update(previous => ({ ...previous, luogoVisioneNote: event.target.value }))} /></label>
        </div>}
      </section>
      <section className="vehicle-required-card"><span className="repairer-eyebrow">4 · Documentazione riparazione</span>
        {documentControl('fattura', values.statoRiparazione === 'riparazione_economia' ? 'Fattura del riparatore · facoltativa' : 'Fattura del riparatore')}
      </section></>}

      {scenario && <PracticeDocumentLinks className="vehicle-photo-card" schedaId={schedaId} title="Foto del veicolo" documentKeys={[scenario === 'demolizione' || values.statoRiparazione === 'non_ripara' ? 'demolitionPhotos' : 'repairPhotos']} onChange={rows => {
        setArchiveDocuments(rows);
        const photo = latestDocumentFor(rows, documentDefinition('repairPhotos'));
        update(previous => ({ ...previous, fotoLavoriDocumentoId: photo?.id || '', fotoLavoriDocumentoNome: photo?.nome_originale || '' }));
      }} />}

      {scenario === 'demolizione' && <section className="vehicle-required-card"><span className="repairer-eyebrow">3 · Documentazione demolizione</span>{documentControl('cessazione', 'Annotata cessazione dalla circolazione')}<label className="vehicle-cessation-date"><span>Data cessazione dalla circolazione</span><input type="date" value={values.dataCessazioneCircolazione || ''} onChange={event => update(previous => ({ ...previous, dataCessazioneCircolazione: event.target.value }))} /></label></section>}
      </>}

      {showCalculation && <>
      {scenario && <div className="vehicle-calculation-title"><span className="repairer-eyebrow">{scenario === 'riparazione' ? '5' : '4'} · Quantificazione</span><h2>{scenario === 'riparazione' ? 'Danno da riparazione' : 'Danno da demolizione'}</h2></div>}

      {!scenario && <section className="vehicle-decision-card vehicle-calculation-empty"><div><span className="repairer-eyebrow">Quantificazione</span><h2>Decisione sul veicolo ancora da definire</h2><p>Prima scegli Riparazione o Demolizione nella scheda Gestione veicolo.</p></div></section>}

      {/* Form per Demolizione */}
      {scenario === 'demolizione' && (
        <div className="form-container">
          <div className="input-row">
            <div className="input-label">Valore commerciale</div>
            <div className="input-value">
              <span className="euro-label">€</span>
              <MoneyInput
                type="text"
                value={valoreCommerciale}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => setValoreCommerciale(formatValue(e.target.value))}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*[,.]?\d*$/.test(value)) {
                    setValoreCommerciale(value);
                  }
                }}
              />
            </div>
          </div>
          <div className="input-row">
            <div className="input-label">Valore relitto</div>
            <div className="input-value">
              <span className="euro-label">€</span>
              <MoneyInput
                type="text"
                value={valoreRelitto}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => setValoreRelitto(formatValue(e.target.value))}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*[,.]?\d*$/.test(value)) {
                    setValoreRelitto(value);
                  }
                }}
              />
            </div>
          </div>
          <div className="input-row">
            <div className="input-label">Immatricolazione</div>
            <div className="input-value">
              <span className="euro-label">€</span>
              <MoneyInput
                type="text"
                value={immatricolazione}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => setImmatricolazione(formatValue(e.target.value))}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*[,.]?\d*$/.test(value)) {
                    setImmatricolazione(value);
                  }
                }}
              />
            </div>
          </div>
          <div className="input-row">
            <div className="input-label">F.r.a.m.</div>
            <div className="input-value">
              <span className="euro-label">€</span>
              <MoneyInput
                type="text"
                value={fram}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => setFram(formatValue(e.target.value))}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*[,.]?\d*$/.test(value)) {
                    setFram(value);
                  }
                }}
              />
            </div>
          </div>
          <div className="input-row">
            <div className="input-label">Bollo non goduto</div>
            <div className="input-value">
              <span className="euro-label">€</span>
              <MoneyInput
                type="text"
                value={bolloNonGoduto}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => setBolloNonGoduto(formatValue(e.target.value))}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*[,.]?\d*$/.test(value)) {
                    setBolloNonGoduto(value);
                  }
                }}
              />
            </div>
          </div>
          <div className="input-row">
            <div className="input-label">Spese di demolizione</div>
            <div className="input-value">
              <span className="euro-label">€</span>
              <MoneyInput
                type="text"
                value={speseDemolizione}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => setSpeseDemolizione(formatValue(e.target.value))}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*[,.]?\d*$/.test(value)) {
                    setSpeseDemolizione(value);
                  }
                }}
              />
            </div>
          </div>
          <div className="input-row">
            <div className="input-label">Deposito</div>
            <div className="input-value">
              <span className="euro-label">€</span>
              <MoneyInput
                type="text"
                value={deposito}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => setDeposito(formatValue(e.target.value))}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*[,.]?\d*$/.test(value)) {
                    setDeposito(value);
                  }
                }}
              />
            </div>
          </div>
          <div className="input-row">
            <div className="input-label">Traino</div>
            <div className="input-value">
              <span className="euro-label">€</span>
              <MoneyInput
                type="text"
                value={traino}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => setTraino(formatValue(e.target.value))}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*[,.]?\d*$/.test(value)) {
                    setTraino(value);
                  }
                }}
              />
            </div>
          </div>
          <div className="input-row">
            <div className="input-label">Accessori</div>
            <div className="input-value">
              <span className="euro-label">€</span>
              <MoneyInput
                type="text"
                value={accessori}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => setAccessori(formatValue(e.target.value))}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*[,.]?\d*$/.test(value)) {
                    setAccessori(value);
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Form per Riparazione */}
      {scenario === 'riparazione' && (
        <div className="form-container">
          {/* Selezione della voce (Perizia, Preventivo o Fattura) */}
          <div className="input-row">
            <div className="input-label">Seleziona voce</div>
            <div className="input-value"><SlidingChoice label="Voce per quantificazione riparazione" value={voceSelezionata} onChange={setVoceSelezionata} options={[{value:'preventivo',label:'Preventivo'},{value:'perizia',label:'Perizia'},{value:'fattura',label:'Fattura'}]} /></div>
          </div>

          {/* Perizia */}
          {voceSelezionata === 'perizia' && (
            <div className="input-row">
              <div className="input-label">Perizia</div>
              <div className="input-value">
                <span className="euro-label">€</span>
                <MoneyInput
                  type="text"
                  value={perizia}
                  readOnly
                  title="Valore proveniente dalla fase Perizia"
                />
              </div>
            </div>
          )}

          {/* Preventivo */}
          {voceSelezionata === 'preventivo' && (
            <div className="input-row">
              <div className="input-label">Preventivo</div>
              <div className="input-value">
                <span className="euro-label">€</span>
                <MoneyInput
                  type="text"
                  value={preventivo}
                  onFocus={(e) => e.target.select()}
                  onBlur={(e) => setPreventivo(formatValue(e.target.value))}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*[,.]?\d*$/.test(value)) {
                      setPreventivo(value);
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* Fattura */}
          {voceSelezionata === 'fattura' && (
            <div className="input-row">
              <div className="input-label">Fattura</div>
              <div className="input-value">
                <span className="euro-label">€</span>
                <MoneyInput
                  type="text"
                  value={fattura}
                  onFocus={(e) => e.target.select()}
                  onBlur={(e) => setFattura(formatValue(e.target.value))}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*[,.]?\d*$/.test(value)) {
                      setFattura(value);
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* Altre voci per Riparazione */}
          <div className="input-row">
            <div className="input-label">Spese noleggio</div>
            <div className="input-value">
              <span className="euro-label">€</span>
              <MoneyInput
                type="text"
                value={speseNoleggio}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => setSpeseNoleggio(formatValue(e.target.value))}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*[,.]?\d*$/.test(value)) {
                    setSpeseNoleggio(value);
                  }
                }}
              />
            </div>
          </div>
          <div className="input-row">
            <div className="input-label">Fermo tecnico</div>
            <div className="input-value">
              <span className="euro-label">€</span>
              <MoneyInput
                type="text"
                value={fermoTecnico}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => setFermoTecnico(formatValue(e.target.value))}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*[,.]?\d*$/.test(value)) {
                    setFermoTecnico(value);
                  }
                }}
              />
            </div>
          </div>
          <div className="input-row">
            <div className="input-label">Traino</div>
            <div className="input-value">
              <span className="euro-label">€</span>
              <MoneyInput
                type="text"
                value={trainoRiparazione}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => setTrainoRiparazione(formatValue(e.target.value))}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*[,.]?\d*$/.test(value)) {
                    setTrainoRiparazione(value);
                  }
                }}
              />
            </div>
          </div>
          <div className="input-row">
            <div className="input-label">Svalutazione</div>
            <div className="input-value">
              <span className="euro-label">€</span>
              <MoneyInput
                type="text"
                value={svalutazione}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => setSvalutazione(formatValue(e.target.value))}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*[,.]?\d*$/.test(value)) {
                    setSvalutazione(value);
                  }
                }}
              />
            </div>
          </div>
          <div className="input-row">
            <div className="input-label">Accessori</div>
            <div className="input-value">
              <span className="euro-label">€</span>
              <MoneyInput
                type="text"
                value={accessoriRiparazione}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => setAccessoriRiparazione(formatValue(e.target.value))}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*[,.]?\d*$/.test(value)) {
                    setAccessoriRiparazione(value);
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

      {scenario && <>
      {/* Linea di separazione */}
      <hr className="separator" />

      {/* Sezione comune: Totale, Definizione, Acconto, Integrazione, Onorari */}
      <div className="common-section">
        <div className="input-row calculated-total">
          <div className="input-label">Totale lordo</div>
          <div className="input-value">
            <span className="euro-label">€</span>
            <span>{scenario === 'demolizione' ? totaleDemolizione : totaleRiparazione}</span>
          </div>
        </div>
        <div className="input-row">
          <div className="input-label">Responsabilità della controparte</div>
          <div className="input-value"><span>{responsibilityDefined ? `${responsibilityNumber.toLocaleString('it-IT', { useGrouping: 'always', maximumFractionDigits: 2 })}%` : 'Da definire'}</span></div>
        </div>
        <div className="input-row payable-total">
          <div className="input-label">Danno spettante</div>
          <div className="input-value">{totaleSpettante === null ? <span>—</span> : <><span className="euro-label">€</span><span>{totaleSpettante}</span></>}</div>
        </div>
        <div className="input-row">
          <div className="input-label">Definizione</div>
          <div className="input-value">
            <span className="euro-label">€</span>
            <MoneyInput
              type="text"
              value={definizione}
              onFocus={(e) => e.target.select()}
              onBlur={(e) => setDefinizione(formatValue(e.target.value))}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*[,.]?\d*$/.test(value)) {
                  setDefinizione(value);
                }
              }}
            />
          </div>
        </div>
        <div className="input-row">
          <div className="input-label">Acconto</div>
          <div className="input-value">
            <span className="euro-label">€</span>
            <MoneyInput
              type="text"
              value={acconto}
              onFocus={(e) => e.target.select()}
              onBlur={(e) => setAcconto(formatValue(e.target.value))}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*[,.]?\d*$/.test(value)) {
                  setAcconto(value);
                }
              }}
            />
          </div>
        </div>
        <div className="input-row integration-total">
          <div className="input-label">Saldo</div>
          <div className="input-value">
            <span className="euro-label">€</span>
            <span>{integrazione}</span>
          </div>
        </div>
        <div className="input-row">
          <div className="input-label">Onorari</div>
          <div className="input-value">
            <span className="euro-label">€</span>
            <MoneyInput
              type="text"
              value={onorari}
              onFocus={(e) => e.target.select()}
              onBlur={(e) => setOnorari(formatValue(e.target.value))}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*[,.]?\d*$/.test(value)) {
                  setOnorari(value);
                }
              }}
            />
          </div>
        </div>
      </div>
      <section className={`vehicle-settlement-card ${settlement ? 'registered' : ''}`}>
        {settlement ? <>
          <div><span>Definizione del danno veicolo</span><strong>Danno concordato a € {Number(settlement.importo || 0).toLocaleString('it-IT', { useGrouping: 'always', minimumFractionDigits: 2 })}</strong><small>{settlement.beneficiario} · {new Date(`${settlement.data}T12:00:00`).toLocaleDateString('it-IT')}{Number(settlement.onorari || 0) > 0 ? ` · Onorari € ${Number(settlement.onorari).toLocaleString('it-IT', { useGrouping: 'always', minimumFractionDigits: 2 })}` : ''}</small></div>
          <div className="vehicle-settlement-numbers"><span>Acconto <b>€ {Number(settlement.acconto || 0).toLocaleString('it-IT', { useGrouping: 'always', minimumFractionDigits: 2 })}</b></span><span>Saldo <b>€ {Number(settlement.saldo || 0).toLocaleString('it-IT', { useGrouping: 'always', minimumFractionDigits: 2 })}</b></span></div>
          {Number(settlement.saldo || 0) > 0 ? <button type="button" onClick={openPayment}>Registra pagamento</button> : <span className="vehicle-settlement-complete">Pagamento completato</span>}
        </> : <><div><span>Chiusura economica</span><strong>Il danno al veicolo non è ancora stato definito</strong><small>Compila definizione, acconto, saldo e onorari; poi conferma il riepilogo.</small></div><button type="button" onClick={openDefinition} disabled={numericValue(definizione) <= 0} title={numericValue(definizione) <= 0 ? 'Inserisci prima la definizione' : ''}>Registra definizione del danno veicolo</button></>}
      </section>
      </>}
      </>}

      {showCalculation && settlementEditor && <div className="repairer-overlay" role="presentation" onMouseDown={event => event.target === event.currentTarget && setSettlementEditor(null)}>
        <div className="repairer-modal vehicle-settlement-modal" role="dialog" aria-modal="true" aria-labelledby="settlement-title">
          <div className="repairer-modal-head"><div><span className="repairer-eyebrow">Danno al veicolo</span><h2 id="settlement-title">{settlementEditor.type === 'definition' ? 'Conferma la definizione' : 'Registra pagamento'}</h2></div><button type="button" className="repairer-close" onClick={() => setSettlementEditor(null)} aria-label="Chiudi">×</button></div>
          {settlementEditor.type === 'definition' && <div className="vehicle-settlement-confirmation"><div><span>Danno spettante</span><strong>€ {totaleSpettante || '0,00'}</strong></div><div><span>Definizione</span><strong>€ {formatValue(settlementEditor.importo)}</strong></div><div><span>Acconto</span><strong>− € {formatValue(settlementEditor.acconto)}</strong></div><div className="balance"><span>Saldo</span><strong>€ {integrazione}</strong></div><div><span>Onorari</span><strong>€ {formatValue(settlementEditor.onorari)}</strong></div></div>}
          <div className="vehicle-settlement-form">
            {settlementEditor.type === 'payment' && <label><span>Importo</span><div><b>€</b><MoneyInput autoFocus inputMode="decimal" value={settlementEditor.importo} onChange={event => setSettlementEditor(previous => ({ ...previous, importo: event.target.value }))} /></div></label>}
            <label><span>Data</span><input autoFocus={settlementEditor.type === 'definition'} type="date" value={settlementEditor.data} onChange={event => setSettlementEditor(previous => ({ ...previous, data: event.target.value }))} /></label>
            {settlementEditor.type === 'definition' && <label><span>Beneficiario</span><input value={settlementEditor.beneficiario} readOnly title="Determinato automaticamente dalla titolarità del credito" /></label>}
            <label className="wide"><span>Nota facoltativa</span><textarea value={settlementEditor.note} onChange={event => setSettlementEditor(previous => ({ ...previous, note: event.target.value }))} /></label>
          </div>
          {settlementError && <p className="repairer-error">{settlementError}</p>}
          <div className="repairer-actions"><button type="button" onClick={() => setSettlementEditor(null)}>Annulla</button><button type="button" className="primary" onClick={saveSettlement} disabled={settlementBusy}>{settlementBusy ? 'Salvataggio…' : 'Conferma'}</button></div>
        </div>
      </div>}

      {showManagement && expertOpen && (
        <div className="repairer-overlay" role="presentation" onMouseDown={event => event.target === event.currentTarget && setExpertOpen(false)}>
          <div className="repairer-modal" role="dialog" aria-modal="true" aria-labelledby="expert-title">
            <div className="repairer-modal-head"><div><span className="repairer-eyebrow">Rubrica periti</span><h2 id="expert-title">Scegli il perito</h2></div><button type="button" className="repairer-close" onClick={() => setExpertOpen(false)} aria-label="Chiudi">×</button></div>
            <label className="repairer-field"><span>Cerca in Rubrica</span><input value={expertSearch} onChange={event => setExpertSearch(event.target.value)} placeholder="Nome, studio, città…" /></label>
            <div className="repairer-results">
              {experts.slice(0, 10).map(item => {
                const phone = item.telefoni?.[0]?.telefono; const email = item.emails?.[0]?.email;
                return <div key={item.id} className={Number(expertDraft.peritoId) === Number(item.id) ? 'selected' : ''}>
                  <button type="button" className="repairer-select" onClick={() => setExpertDraft({ peritoId: item.id, peritoNome: item.nome })}><strong>{item.nome}</strong><small>{[item.referente, item.citta].filter(Boolean).join(' · ') || 'Recapiti disponibili in Rubrica'}</small></button>
                  <span className="repairer-quick-links">{phone && <a href={`tel:${phone.replace(/[^+\d]/g, '')}`}>{phone}</a>}{email && <a href={`mailto:${email}`}>{email}</a>}</span>
                </div>;
              })}
              {!experts.length && !expertError && <p>Nessun perito trovato.</p>}
            </div>
            <button type="button" className="repairer-add" onClick={() => setCreatingExpert(value => !value)}>+ Nuovo perito</button>
            {creatingExpert && <div className="repairer-new-grid">
              <VehicleContactFields value={newExpert} onChange={setNewExpert}/>
              <button type="button" className="repairer-create" onClick={createExpert}>Crea e seleziona</button>
            </div>}
            {expertError && <p className="repairer-error">{expertError}</p>}
            <div className="repairer-actions"><button type="button" onClick={() => setExpertOpen(false)}>Annulla</button><button type="button" className="primary" onClick={saveExpert} disabled={!expertDraft.peritoId}>Collega perito</button></div>
          </div>
        </div>
      )}
      {showManagement && repairerOpen && (
        <div className="repairer-overlay" role="presentation" onMouseDown={event => event.target === event.currentTarget && setRepairerOpen(false)}>
          <div className="repairer-modal" role="dialog" aria-modal="true" aria-labelledby="repairer-title">
            <div className="repairer-modal-head"><div><span className="repairer-eyebrow">Rubrica riparatori</span><h2 id="repairer-title">Scegli il riparatore</h2></div><button type="button" className="repairer-close" onClick={() => setRepairerOpen(false)} aria-label="Chiudi">×</button></div>
            <label className="repairer-field"><span>Cerca in Rubrica</span><input value={repairerSearch} onChange={event => setRepairerSearch(event.target.value)} placeholder="Nome, città, referente…" /></label>
            <div className="repairer-results">
              {repairers.slice(0, 10).map(item => {
                const phone = item.telefoni?.[0]?.telefono;
                const email = item.emails?.find(contact => String(contact.tipo).toUpperCase() === 'EMAIL')?.email || item.emails?.[0]?.email;
                return <div key={item.id} className={Number(repairerDraft.riparatoreId) === Number(item.id) ? 'selected' : ''}>
                  <button type="button" className="repairer-select" onClick={() => setRepairerDraft(previous => ({ ...previous, riparatoreId: item.id, riparatoreNome: item.nome }))}><strong>{item.nome}</strong><small>{[item.indirizzo, item.citta].filter(Boolean).join(' · ') || 'Indirizzo non indicato'}</small></button>
                  <span className="repairer-quick-links">
                    {phone && <a href={`tel:${phone.replace(/[^+\d]/g, '')}`} title={`Chiama ${phone}`}>{phone}</a>}
                    {email && <a href={`mailto:${email}`} title={`Scrivi a ${email}`}>{email}</a>}
                  </span>
                </div>;
              })}
              {!repairers.length && !repairerError && <p>Nessun riparatore trovata.</p>}
              {repairers.length > 10 && <p className="repairer-more">Altri risultati disponibili: scrivi nome, città o referente per restringere la ricerca.</p>}
            </div>
            <button type="button" className="repairer-add" onClick={() => setCreatingRepairer(value => !value)}>+ Nuovo riparatore</button>
            {creatingRepairer && <div className="repairer-new-grid">
              <QuickAddressInput className="repairer-field repairer-address-quick" addressValue={newRepairer.indirizzo || ''} onApply={address => setNewRepairer(previous => ({ ...previous, ...address }))} />
              {Object.entries({ nome: 'Nome riparatore', referente: 'Referente', citta: 'Città', cap: 'CAP', provincia: 'Provincia', telefono: 'Telefono', email: 'Email' }).map(([key, labelText]) => <label className="repairer-field" key={key}><span>{labelText}</span><input value={newRepairer[key]} onChange={event => setNewRepairer(previous => ({ ...previous, [key]: event.target.value }))} /></label>)}
              <button type="button" className="repairer-create" onClick={createRepairer}>Crea e seleziona</button>
            </div>}
            {repairerError && <p className="repairer-error">{repairerError}</p>}
            <div className="repairer-actions"><button type="button" onClick={() => setRepairerOpen(false)}>Annulla</button><button type="button" className="primary" onClick={saveRepairer} disabled={!repairerDraft.riparatoreId}>Collega riparatore</button></div>
          </div>
        </div>
      )}
      {showManagement && repairerDetailsOpen && repairerDetails && (
        <div className="repairer-overlay" role="presentation" onMouseDown={event => event.target === event.currentTarget && setRepairerDetailsOpen(false)}>
          <div className="repairer-modal repairer-detail-modal" role="dialog" aria-modal="true" aria-labelledby="repairer-detail-title">
            <div className="repairer-modal-head"><div><span className="repairer-eyebrow">{values.modalitaGestioneCredito === 'cessione' ? 'Riparatore cessionario del credito' : 'Scheda Rubrica'}</span><h2 id="repairer-detail-title">{repairerDetails.nome}</h2>{values.modalitaGestioneCredito === 'cessione' && <span className="credit-role-badge">Cessionaria del credito in questa pratica</span>}</div><button type="button" className="repairer-close" onClick={() => setRepairerDetailsOpen(false)} aria-label="Chiudi">×</button></div>
            <div className="repairer-detail-grid">
              <div><span>Referente</span><strong>{repairerDetails.referente || 'Non indicato'}</strong></div>
              <div><span>Partita IVA</span><strong>{repairerDetails.partita_iva || 'Non indicata'}</strong></div>
              <div><span>Codice fiscale</span><strong>{repairerDetails.codice_fiscale || 'Non indicato'}</strong></div>
              <div><span>IBAN</span><strong className="repairer-iban">{repairerDetails.iban || 'Non indicato'}</strong></div>
              <div className="wide"><span>Indirizzo</span><strong>{[repairerDetails.indirizzo, repairerDetails.cap, repairerDetails.citta, repairerDetails.provincia].filter(Boolean).join(', ') || 'Non indicato'}</strong></div>
              <div><span>Telefoni</span>{repairerDetails.telefoni?.length ? repairerDetails.telefoni.map((item, index) => <a key={index} href={`tel:${item.telefono.replace(/[^+\d]/g, '')}`}>{item.tipo ? `${item.tipo}: ` : ''}{item.telefono}</a>) : <strong>Non indicati</strong>}</div>
              <div><span>Email e PEC</span>{repairerDetails.emails?.length ? repairerDetails.emails.map((item, index) => <a key={index} href={`mailto:${item.email}`}>{item.tipo ? `${item.tipo}: ` : ''}{item.email}</a>) : <strong>Non indicate</strong>}</div>
              <div className="wide"><span>Note</span><strong>{repairerDetails.note || 'Nessuna nota'}</strong></div>
            </div>
            <div className="repairer-linked-practices"><span className="repairer-eyebrow">Pratiche collegate ({repairerDetails.numero_pratiche || 0})</span>{repairerDetails.pratiche?.length ? repairerDetails.pratiche.map(item => <a key={item.id} href={`/pratica/${item.id}`}>{item.numero_progressivo || `Pratica ${item.id}`} · {item.tipo} · {item.stato}</a>) : <p>Nessuna pratica collegata.</p>}</div>
            <div className="repairer-actions"><button type="button" className="primary" onClick={() => setRepairerDetailsOpen(false)}>Chiudi</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DannoAuto;

